<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import { Gallery } from 'flowbite-svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import { animate, inView } from '@motionone/dom';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import Quote from '$lib/components/Quote.svelte';
  import Credits from '$lib/components/Credits.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import { projects, getAdjacentProjects } from '$lib/projects';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let engagementRate = 0;
  let interactionTime = 0;
  let visitorCount = 0;

  // Gallery images
  const galleryColumn1 = [
    { 
      src: '/projects/do-you-like-working/exhibition-overview.jpg',
      alt: 'Exhibition overview showing the interactive installation'
    },
    { 
      src: '/projects/do-you-like-working/exhibition-space.jpg',
      alt: 'Full view of the exhibition space'
    },
    { 
      src: '/projects/do-you-like-working/glass-panel-interface.jpg',
      alt: 'Close-up of the interactive glass panel interface'
    },
    { 
      src: '/projects/do-you-like-working/robot-arm-detail.jpg',
      alt: 'Detailed view of the robotic arm'
    },
    { 
      src: '/projects/do-you-like-working/robot-drawing-detail.jpg',
      alt: 'Detail of the robot drawing interaction'
    },
    { 
      src: '/projects/do-you-like-working/robot-interaction.jpg',
      alt: 'Visitor interacting with the robotic installation'
    },
    { 
      src: '/projects/do-you-like-working/robot-writing-sequence.jpg',
      alt: 'Sequence showing the robot writing process'
    }
  ];

  function handleTagClick(tag: string) {
    console.log(`Filter by: ${tag}`);
    // Add filtering logic here
  }

  // Get the current project slug from the URL
  const currentSlug = 'do-you-like-working';
  const navigation = getAdjacentProjects(currentSlug);

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 85, 2000, (val) => engagementRate = val);
          animateValue(0, 15, 1000, (val) => interactionTime = val);
          animateValue(0, 5000, 1500, (val) => visitorCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    return () => {
      metricsObserver.disconnect();
    };
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Do You Like Working?"
    client="Futurium Berlin"
    industry="Culture"
    clientLogo="/logos/Futurium.png"
    product="Interactive Installation"
    heroMedia={{
      type: 'video',
      src: "https://vz-6b226b53-3c8.b-cdn.net/82252ecd-3519-48cb-bd16-78e318d86a9b/playlist.m3u8",
      poster: "/projects/do-you-like-working/project-large.jpg"
    }}
    metrics={[
      { value: 85, label: "Engagement Rate" },
      { value: 15, label: "Interaction Time" },
      { value: 5000, label: "Visitors" }
    ]}
    roles={["Creative Lead", "User Experience Design", "Interaction Design", "Project Management"]}
  />

  <!-- Content -->
  <div class="project-section">
    
    <!-- Large Text Block -->
    <div class="space-y-2 px-16 mb-12 animate-on-view border-t border-b py-6">
      <p class="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-black/90 animate-on-view" style="line-height: 1.3;">
        An interactive conversation with an industrual robot about the future of work.
      </p>
    </div>

    <ChallengeSolution
      challengeText="Create an engaging and thought-provoking installation that makes the complex topic of automation and the future of work accessible to a broad audience, while fostering meaningful dialogue between humans and machines."
      solutionText="A unique interactive installation featuring a robotic arm that writes and draws on a glass panel, creating a playful yet meaningful dialogue with visitors about human-machine collaboration."
      className="mb-16"
    />
    <!-- Quote Section -->
    <Quote
      text="Futurium is a House of Futures. Here, everything revolves around the question: how do we want to live?"
      color="hsla(226, 65%, 35%, 1)"
    />

    <!-- Key Features -->
    <div class="w-full">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 mb-16 bg-black">
        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>'
          title="Custom Soft,- and Hardware"
          description="Research and development of custom software and hardware enabling a unique experience"
          delay={0}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>'
          title="Modular Interaction Scenes"
          description="Scenes can be combined to create new interactive experiences"
          delay={0.2}
        />

        <FeatureCell
          icon='<svg class="w-8 h-8 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>'
          title="Most Visited Piece in the Exhibition"
          description="The installation is one of the most visited permanent pieces of the Futurium"
          delay={0.4}
        />
      </div>
    </div>

    <!-- Exhibition Space -->
    <div class="w-full mb-16 animate-on-view">
      <img 
        src="/projects/do-you-like-working/exhibition-space.jpg" 
        alt="The exhibition space" 
        class="w-full h-auto"
      />
      <p class="text-sm text-gray-600 mt-2 text-center">The exhibition space</p>
    </div>

    <!-- Interactive Elements -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-start-3 col-span-8">
          <p class="text-lg text-black/90 leading-relaxed mb-8">
            A custom-made interactive glass panel serves as communication device between visitors and robot. The robot writes questions and draws user interfaces on the glass panel.
          </p>
          <p class="text-lg text-black/90 leading-relaxed">
            The visitor on the other side can respond via drawn buttons and emojis. Answers are followed by counter-questions – an interactive dialogue about the future of work begins.
          </p>
        </div>
      </div>
    </div>

    <!-- Full Width Image Section -->
    <div class="w-full mb-4 animate-on-view" data-delay="8">
      <div class="w-full">
        <img 
          src="/projects/do-you-like-working/glass-panel-interface.jpg" 
          alt="Interactive Robot Interface" 
          class="w-full h-auto"
          style="image-rendering: crisp-edges;"
        />
      </div>
    </div>

    <!-- Robot Interaction -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16 animate-on-view">
      <div>
        <img 
          src="/projects/do-you-like-working/robot-writing-sequence.jpg" 
          alt="Multiple scenes of robot writing" 
          class="w-full h-auto"
        />
        <p class="text-sm text-gray-600 mt-2 text-center">Multiple scenes keep the robot busy writing questions and reacting to the answers.</p>
      </div>
      <div>
        <img 
          src="/projects/do-you-like-working/robot-interaction.jpg" 
          alt="Robot interaction detail" 
          class="w-full h-auto"
        />
        <p class="text-sm text-gray-600 mt-2 text-center">The robot communicates its willingness to do monotonous work via a drawing. Percentage values show the preferences of the visitors so far.</p>
      </div>
    </div>

    <!-- Text Section -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-lg text-black/90 leading-relaxed col-start-3 col-span-8">
          The installation "Anyone Still Working Here?" invites visitors of the exhibition at the Futurium to negotiate the details of this undertaking with a prominent representative of the pro-robot movement: An industrial robot arm.
        </p>
      </div>
    </div>

    <!-- Video -->
    <div class="w-full mb-16 animate-on-view">
      <VideoPlayer
        src="https://vz-6b226b53-3c8.b-cdn.net/55a13f2c-ef98-478c-a4e9-57c0cdebfd01/playlist.m3u8"
        poster="/projects/do-you-like-working/project-large.jpg"
        autoplay={true}
        loop={true}
        controls={true}
        muted={true}
      />
    </div>

    <!-- Commission Info -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-2xl text-black/90 leading-relaxed col-start-3 col-span-8">
          <em>Do You Like Working?</em> is part of the permanent exhibition of the <a href="https://futurium.de/en?page=1" class="text-accent-color hover:underline">Futurium</a>, a House of Futures.
        </p>
      </div>
    </div>
  </div>

  <div class="container mx-auto px-8">
    <Credits
      client={{ label: "Commissioned by", name: "Futurium", url: "https://futurium.de/en?page=1" }}
      team={[
        { name: "Matthias Maercks", role: "Film and Camera", url: "https://www.tableau-films.de/" },
        { name: "Hannah Sieh", role: "Actress", url: "http://hannahsieh.de/" },
        { name: "interactive scape", role: "Thanks to", url: "https://www.interactive-scape.com/" }
      ]}
    />
  </div>

  {#if navigation}
    <ProjectNav 
      currentProject={{
        title: navigation.currentProject.title,
        category: navigation.currentProject.category,
        link: navigation.currentProject.link
      }}
      allProjects={projects}
    />
  {/if}

  <Footer />
</div> 